'use client';

import { CSS_BREAKPOINTS } from '@sbercloud/uikit-product-utils';
import { ItemId, ItemProps, List } from '@snack-uikit/list';
import { Scroll } from '@snack-uikit/scroll';
import cn from 'classnames';
import { useRouter } from 'next/navigation';

import { Condition } from '@/components/static';
import { useAppNavigationCtx, useLayoutCtx, useServerDataCtx, useTOCDataCtx } from '@/hooks/ctx';
import { useLayoutType } from '@/root/src/hooks';
import { Predicate } from '@/types/common';
import { isEmpty, isNil, isString } from '@/utils/common';
import {
  checkIsActiveNavigationItem,
  getActiveElementParentIds,
  getScrollToAnchorOffsetDelta,
  scrollToAnchor,
} from '@/utils/navigation';
import { skipHashTag } from '@/utils/strings';

import s from './styles.module.scss';

type TableOfContentProps = {
  customList?: ItemProps[];
  className?: string;
  getActiveElementParentIdsPred?: Predicate<ItemId[]>;
};

export function TableOfContent({ customList, className, getActiveElementParentIdsPred }: TableOfContentProps) {
  const { activeTOCElement, handleChangeActiveTOCElement } = useTOCDataCtx();
  const router = useRouter();
  const { layoutWrapperRef } = useLayoutCtx();
  const { isMainPage } = useAppNavigationCtx();
  const { headers } = useServerDataCtx();
  const layoutType = useLayoutType(CSS_BREAKPOINTS, headers.xLayoutType);

  const isActiveTOCItem: Predicate<ItemId[]> = id =>
    checkIsActiveNavigationItem('TOC', { currentItemID: id, selectedAnchor: activeTOCElement });

  if (isEmpty(customList)) return null;

  const parentIds = getActiveElementParentIds({
    list: customList,
    predicate: getActiveElementParentIdsPred || isActiveTOCItem,
  });

  const handleChangeAnchor = (id: ItemId) => {
    if (!isString(id)) return;
    handleChangeActiveTOCElement(skipHashTag(id));

    router.replace(id, { scroll: false });
    scrollToAnchor(skipHashTag(id), {
      layoutWrapperRef,
      offsetDelta: getScrollToAnchorOffsetDelta(layoutType, isMainPage),
    });
  };

  return (
    <div className={cn(s.tabs_wrapper, className)}>
      <Scroll size='s' className={s.list_scroll}>
        <Condition when={!isNil(activeTOCElement)}>
          <List
            scroll={false}
            selection={{
              mode: 'single',
              value: `#${activeTOCElement}`,
              onChange: handleChangeAnchor,
            }}
            collapse={{ defaultValue: parentIds }}
            items={customList}
            size='m'
          />
        </Condition>
      </Scroll>
    </div>
  );
}
